import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  TOKEN_KEY,
  getToken,
  storeSession,
  clearSession,
  setAuthHeader,
  ensureFreshSession,
} from '@/services/authSession';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Keep the Authorization header in sync with what is already persisted.
setAuthHeader(getToken());

const RETRY_DELAYS_MS = [1500, 3000];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const keepSessionRef = useRef(false);

  /**
   * Restores the session on boot. A failure only means "logged out" when the
   * server explicitly says so (401/403); transient errors (offline, cold start,
   * timeout) are retried and, if they persist, the stored session is kept for
   * the next attempt instead of kicking the user back to the login screen.
   */
  const checkAuth = useCallback(async () => {
    keepSessionRef.current = false;
    try {
      const hasSession = await ensureFreshSession();
      if (!hasSession) {
        clearSession();
        setUser(false);
        return;
      }

      for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
        try {
          const { data } = await axios.get(`${API}/auth/me`, { _skipAuthRefresh: true });
          setUser(data);
          return;
        } catch (error) {
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            clearSession();
            setUser(false);
            return;
          }
          if (attempt < RETRY_DELAYS_MS.length) {
            await sleep(RETRY_DELAYS_MS[attempt]);
            continue;
          }
          keepSessionRef.current = true;
          setUser(false);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // A device that slept or lost connectivity often comes back with an expired
  // access token; refresh as soon as the network is available again.
  useEffect(() => {
    const handleOnline = () => {
      if (getToken()) checkAuth();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { _skipAuthRefresh: true });
      storeSession(data);
      const currentUser = data.user || (data.id ? data : null);
      if (currentUser) {
        setUser(currentUser);
      } else {
        await checkAuth();
      }
      return { success: true };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    keepSessionRef.current = false;
    try {
      await axios.post(`${API}/auth/logout`, {}, { _skipAuthRefresh: true });
    } catch {}
    clearSession();
    setUser(false);
  };

  const isSessionKept = useCallback(() => keepSessionRef.current, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, isSessionKept, TOKEN_KEY }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
